import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@ankr.com/ui';
import { Typography } from '@mui/material';
import { ModalProps } from '@mui/material/Modal';
import BigNumber from 'bignumber.js';
import { makeStyles } from 'tss-react/mui';

import { useDepositMutation } from 'modules/account';
import { Modal, NumberField, useChainData, ZERO } from 'modules/common';
import { useTranslation } from 'modules/i18n';
import {
  useGetAccountAddressQuery,
  useGetNativeBalanceQuery,
} from 'modules/store';

import translation from './depositModal.translation';

interface Props extends Omit<ModalProps, 'children'> {
  onClose: () => void;
}

export function DepositModal(props: Props): JSX.Element {
  const { t, keys } = useTranslation(translation);
  const chainData = useChainData();
  const { classes } = useStyles();

  const {
    control,
    watch,
    formState: { errors },
  } = useForm<{ deposit: string }>({
    mode: 'onChange',
    defaultValues: {
      deposit: '',
    },
  });

  const { data: accountAddress = '' } = useGetAccountAddressQuery();
  const { data: nativeBalance } = useGetNativeBalanceQuery({ accountAddress });
  const [deposit, { isLoading }] = useDepositMutation();

  const depositFormValue = watch('deposit');
  const depositAmount = useMemo(() => {
    const convertedValue = new BigNumber(depositFormValue);

    return convertedValue.isNaN() ? ZERO : convertedValue;
  }, [depositFormValue]);

  const { onClose } = props;

  const depositDisabled = depositAmount?.isZero() || !!errors?.deposit;

  return (
    <Modal {...props}>
      <div className={classes.container}>
        <Typography variant="h6">{t(keys.title)}</Typography>

        <Typography variant="subtitle3" className={classes.label}>
          {t(keys.balanceLabel, { unit: chainData?.tokenName })}
        </Typography>

        <form
          data-tid="create-task-deposit-modal-form"
          className={classes.panel}
        >
          <NumberField
            data-tid="create-task-deposit-modal-form-input"
            control={control}
            name="deposit"
            balance={nativeBalance}
            error={!!errors?.deposit}
            sx={{ flex: 1 }}
            variant="filled"
          />

          <LoadingButton
            data-tid="create-task-deposit-modal-form-button"
            onClick={async () => {
              await deposit({
                accountAddress,
                amount: depositAmount,
                chainId: chainData?.chainId ?? null,
              });
              onClose();
            }}
            disabled={depositDisabled}
            loading={isLoading}
            type="button"
            size="large"
            sx={{ minWidth: 128 }}
          >
            {t(keys.submit)}
          </LoadingButton>
        </form>
      </div>
    </Modal>
  );
}

export const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  panel: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(4),
    marginTop: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'initial',
    },
  },
  label: {
    '&&': {
      marginTop: theme.spacing(10),

      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(8),
      },
    },
  },
}));
